import type { StaticImageData } from 'next/image';
import whiteBg from '@/public/pricing-section-v2/white-bg.png';
import greenBg from '@/public/pricing-section-v2/green-bg.png';
import blackBg from '@/public/pricing-section-v2/black-bg.png';
import {
  GROQButton,
  GROQCTA,
  IPricingAsset,
  IPricingCategory,
  IPricingSectionV2,
  IPricingSpecDefinition,
  IPricingTier,
  IPricingTierCTA,
  IPricingTierFee,
  IPricingTierSpec,
} from '@/sanity/lib/definitions';
import type { II18nText } from '@/sanity/globals/I18n';
import { IPricingV2Discount } from '@/sanity/templates';

export type TierTheme = 'light-green' | 'green' | 'dark';

const NEW_BADGE_LABELS: Record<string, string> = {
  en: 'New',
  br: 'Nova',
  de: 'Neu',
};

export function resolveNewBadgeLabel(language?: string): string {
  if (!language) return NEW_BADGE_LABELS.en;
  return NEW_BADGE_LABELS[language] ?? NEW_BADGE_LABELS.en;
}

export type ThemeStyles = {
  headerBg: string;
  headerText: string;
  tierChipBg: string;
  categoryChipBorder: string;
  categoryChipText: string;
  headerImage?: StaticImageData;
  headerImageBlend?: string;
};

/**
 * "light-green" key kept for backward-compatibility with existing docs,
 * but visually renders as a light gray (Advanced tier per Figma 4454-439).
 */
export const TIER_THEME_STYLES: Record<TierTheme, ThemeStyles> = {
  'light-green': {
    headerBg: 'bg-[#f5f5f5]',
    headerText: 'text-black',
    tierChipBg: 'bg-[#228e71]',
    categoryChipBorder: 'border-black/10',
    categoryChipText: 'text-black',
    headerImage: whiteBg,
    headerImageBlend: 'mix-blend-difference',
  },
  green: {
    headerBg: 'bg-[#65a594]',
    headerText: 'text-white',
    tierChipBg: 'bg-[#35c299]',
    categoryChipBorder: 'border-white/20',
    categoryChipText: 'text-white',
    headerImage: greenBg,
    headerImageBlend: 'mix-blend-luminosity',
  },
  dark: {
    headerBg: 'bg-[#010101]',
    headerText: 'text-white',
    tierChipBg: 'bg-[#228e71]',
    categoryChipBorder: 'border-white/20',
    categoryChipText: 'text-white',
    headerImage: blackBg,
    headerImageBlend: 'mix-blend-luminosity',
  },
};

/** Which side of a tier's specs toggle a plan represents. */
export type PlanVariant = 'primary' | 'secondary';

export type FlatPlan = {
  assetKey: string;
  assetLabel: string;
  categoryKey: string;
  categoryName: string;
  categorySubLabel?: string;
  tier: IPricingTier;
  planId: string;
  /** Only set when the tier was split by `splitToggleVariants`. */
  variant?: PlanVariant;
  /** Toggle tab label for this variant, e.g. "Daily Loss Limit". */
  variantLabel?: string;
  variantLabelI18n?: IPricingTier['labelI18n'];
};

export function flattenPlans(
  section: IPricingSectionV2,
  options?: {
    /**
     * Emit one plan per specs-toggle tab instead of one per tier. The two tabs
     * are distinct products (own price, own checkout link, own loss-limit rule),
     * so surfaces that let users pick a single plan — the comparison table —
     * must be able to address them individually.
     */
    splitToggleVariants?: boolean;
  },
): FlatPlan[] {
  const plans: FlatPlan[] = [];
  section.assets?.forEach((asset) => {
    asset.categories?.forEach((category) => {
      category.tiers?.forEach((tier) => {
        const base = {
          assetKey: asset.key ?? '',
          assetLabel: asset.label ?? '',
          categoryKey: category.key ?? '',
          categoryName: category.name ?? '',
          categorySubLabel: category.subLabel,
          tier,
        };
        const baseId = `${asset.key}:${category.key}:${tier.key}`;
        const toggle = tier.specsToggle;

        if (options?.splitToggleVariants && tier.hasSpecsToggle && toggle) {
          plans.push({
            ...base,
            planId: `${baseId}:primary`,
            variant: 'primary',
            variantLabel: toggle.primaryLabel,
            variantLabelI18n: toggle.primaryLabelI18n,
          });
          plans.push({
            ...base,
            planId: `${baseId}:secondary`,
            variant: 'secondary',
            variantLabel: toggle.secondaryLabel,
            variantLabelI18n: toggle.secondaryLabelI18n,
          });
          return;
        }

        plans.push({ ...base, planId: baseId });
      });
    });
  });
  return plans;
}

/* ── Specs toggle: per-tab rows ──────────────────────────────────────────────
 *
 * The two tabs of a specs toggle are different products (e.g. Futures Classic
 * vs Classic DLL), so they can differ in more than the one loss row the toggle
 * was originally built for: a KPI may carry a different number on each tab, or
 * exist on only one of them.
 *
 * Both the tier cards and the comparison table have to answer the same two
 * questions, so the rules live here rather than in each renderer.
 *
 * Everything degrades to the pre-existing behaviour: a row with no `showIn`
 * and no `secondaryValue` resolves exactly as it did before these fields.
 */

/** Whether a spec row renders on the currently active tab. */
export function isSpecOnActiveTab(
  spec: IPricingTierSpec,
  tier: IPricingTier,
  secondaryActive: boolean,
): boolean {
  if (!tier.hasSpecsToggle) return true;

  // The explicit per-row setting wins: it is the only way to say "this KPI
  // belongs to one product but not the other".
  if (spec.showIn === 'primary') return !secondaryActive;
  if (spec.showIn === 'secondary') return secondaryActive;

  // Legacy pairing — the toggle swaps its two named rows.
  const toggle = tier.specsToggle;
  if (toggle) {
    const isToggleRow =
      spec.key === toggle.primaryRowKey || spec.key === toggle.secondaryRowKey;
    if (isToggleRow) {
      const activeKey = secondaryActive ? toggle.secondaryRowKey : toggle.primaryRowKey;
      return spec.key === activeKey;
    }
  }

  return true;
}

/**
 * The value a spec row shows on the active tab. Per-row override first, then
 * the older single-row override on the toggle itself, then the stored value.
 */
export function resolveSpecTabValue(
  spec: IPricingTierSpec,
  tier: IPricingTier,
  secondaryActive: boolean,
): { value?: string; valueI18n?: II18nText } {
  if (!tier.hasSpecsToggle || !secondaryActive) {
    return { value: spec.value, valueI18n: spec.valueI18n };
  }

  if (spec.secondaryValue) {
    return { value: spec.secondaryValue, valueI18n: spec.secondaryValueI18n };
  }

  const toggle = tier.specsToggle;
  if (toggle && spec.key === toggle.secondaryRowKey && toggle.secondaryValue) {
    return { value: toggle.secondaryValue, valueI18n: toggle.secondaryValueI18n };
  }

  return { value: spec.value, valueI18n: spec.valueI18n };
}

/** The fee this plan charges — the secondary tab has its own price. */
export function planFee(plan: FlatPlan): IPricingTierFee | undefined {
  return plan.variant === 'secondary'
    ? (plan.tier.secondaryFee ?? plan.tier.fee)
    : plan.tier.fee;
}

/** The checkout CTA for this plan — the secondary tab is a different SKU. */
export function planCta(plan: FlatPlan): IPricingTierCTA | undefined {
  return plan.variant === 'secondary'
    ? (plan.tier.secondaryCtaPrimary ?? plan.tier.ctaPrimary)
    : plan.tier.ctaPrimary;
}

/**
 * Resolves one comparison row for a plan.
 *
 * Two rules beyond a plain `specs` lookup:
 *
 * 1. Funded fallback — KPIs like Profit Split and Payout Every only live in
 *    `fundedSpecs` on challenge tiers, but the comparison is a product overview,
 *    not a card face, so it should show every KPI regardless of which side it
 *    was authored on. The challenge value always wins where both exist, since
 *    keys such as `maxDrawdown` legitimately differ between the two states.
 *
 * 2. Toggle isolation — a plan split by `splitToggleVariants` owns only its own
 *    loss-limit row; the other variant's row reads empty instead of showing a
 *    rule that does not apply to this product.
 */
export function planSpecValue(
  plan: FlatPlan,
  key: string | undefined,
): IPricingTierSpec | undefined {
  if (!key) return undefined;

  const fromSpecs = plan.tier.specs?.find((s) => s.key === key);
  const toggle = plan.tier.specsToggle;

  // Each toggle tab is its own column here, so a row that belongs to the other
  // tab has no value and the table falls back to its empty-value label.
  if (plan.variant) {
    const secondary = plan.variant === 'secondary';

    if (fromSpecs) {
      if (!isSpecOnActiveTab(fromSpecs, plan.tier, secondary)) return undefined;
      const resolved = resolveSpecTabValue(fromSpecs, plan.tier, secondary);
      if (resolved.value) return { key, ...resolved };
    } else if (toggle) {
      // Key not authored in `specs` at all — keep the legacy pairing rule so a
      // loss row that only exists on the funded side still hides on the tab it
      // does not belong to.
      const foreignRowKey = secondary ? toggle.primaryRowKey : toggle.secondaryRowKey;
      if (key === foreignRowKey) return undefined;
    }
  }

  if (fromSpecs?.value) return fromSpecs;

  const fromFunded = plan.tier.fundedSpecs?.find((s) => s.key === key);
  if (fromFunded?.value && plan.variant) {
    // Funded rows carry the same per-tab fields, so a KPI authored only on the
    // funded side still resolves per product.
    const secondary = plan.variant === 'secondary';
    if (!isSpecOnActiveTab(fromFunded, plan.tier, secondary)) return undefined;
    const resolved = resolveSpecTabValue(fromFunded, plan.tier, secondary);
    if (resolved.value) return { key, ...resolved };
  }

  return fromFunded?.value ? fromFunded : fromSpecs;
}

export function getSpecDefinition(
  catalog: IPricingSpecDefinition[] | undefined,
  key: string | undefined,
): IPricingSpecDefinition | undefined {
  if (!catalog || !key) return undefined;
  return catalog.find((s) => s.key === key);
}

export function getSpecValue(tier: IPricingTier | undefined, key: string | undefined) {
  if (!tier || !key) return undefined;
  return tier.specs?.find((s) => s.key === key);
}

export function findAsset(section: IPricingSectionV2, key: string): IPricingAsset | undefined {
  return section.assets?.find((a) => a.key === key);
}

export function findCategory(asset: IPricingAsset | undefined, key: string): IPricingCategory | undefined {
  return asset?.categories?.find((c) => c.key === key);
}

/**
 * Resolves the discount percent that should apply to a given asset+category pair.
 * Rapid inherits the Instant Funding %. Returns undefined if no discount is set.
 */
export function resolveTierDiscountPercent(
  assetKey: string | undefined,
  categoryKey: string | undefined,
  discount?: IPricingV2Discount,
): number | undefined {
  if (!discount || !assetKey || !categoryKey) return undefined;
  const isCore = categoryKey === 'core';
  const isRapidOrInstant = categoryKey === 'rapid' || categoryKey === 'instant' || categoryKey === 'bolt';

  let value: number | undefined;
  if (assetKey === 'futures') {
    value = isCore
      ? discount.futuresCoreDiscountPercent
      : isRapidOrInstant
        ? discount.futuresInstantFundingDiscountPercent
        : undefined;
  } else if (assetKey === 'cfd_crypto') {
    value = isCore
      ? discount.cfdCryptoCoreDiscountPercent
      : isRapidOrInstant
        ? discount.cfdCryptoInstantFundingDiscountPercent
        : undefined;
  }
  return value && value > 0 ? value : undefined;
}

/**
 * Applies a discount % to a price string like "$1,099" and returns the discounted
 * value in the same format ("$330"). Preserves any non-numeric prefix/suffix
 * (currency symbol, "k", etc.). Rounds to nearest whole for integer inputs.
 */
export function applyDiscountToPrice(price: string | undefined, percent: number): string | undefined {
  if (!price) return undefined;
  const match = price.match(/^([^\d]*)(\d[\d,.]*)(.*)$/);
  if (!match) return price;
  const [, prefix, numStr, suffix] = match;
  const num = parseFloat(numStr.replace(/,/g, ''));
  if (isNaN(num)) return price;
  const discounted = num * (1 - percent / 100);
  const formatted = Number.isInteger(num)
    ? Math.round(discounted).toLocaleString('en-US')
    : discounted.toFixed(2);
  return `${prefix}${formatted}${suffix}`;
}

/**
 * Adapts a tier's simple `{ title, href, variant }` CTA into the shape
 * `CTAWrapper` expects (a GROQCTA), so we get the same link/anchor/new-tab
 * handling as the rest of the project without introducing a full CTA reference.
 */
export function tierCtaToGroqCta(
  cta: IPricingTierCTA | undefined,
  resolvedTitle?: string,
): GROQCTA | null {
  const href = cta?.href?.trim();
  if (!href) return null;

  // Real anchor targets like "#getting-started" — scroll behavior
  if (href.startsWith('#') && href.length > 1) {
    return {
      name: '',
      title: resolvedTitle,
      action: 'anchor',
      anchor: href.slice(1),
    };
  }

  // Everything else (bare "#", http, mailto, tel, /paths) → treat as link so
  // CTAWrapper renders an <a>/<Link> and the button stays clickable.
  const isProtocolExternal = /^https?:\/\//i.test(href) || href.startsWith('mailto:') || href.startsWith('tel:');
  const isInternalPath = href.startsWith('/');
  const treatAsExternal = isProtocolExternal || !isInternalPath;

  return {
    name: '',
    title: resolvedTitle,
    action: 'link',
    link: {
      externalLink: treatAsExternal,
      url: href,
      openInNewTab: isProtocolExternal,
      internalLink: isInternalPath
        ? ({ settings: { slug: { current: href } } } as never)
        : undefined,
    },
  };
}

/**
 * Wraps a tier's CTA into a full `GROQButton` shape so the project's `<CTAButton>`
 * component (with its slide-up hover animation) can render it.
 */
export function tierCtaToGroqButton(
  cta: IPricingTierCTA | undefined,
  resolvedTitle?: string,
): GROQButton | null {
  const groqCta = tierCtaToGroqCta(cta, resolvedTitle);
  if (!groqCta) return null;

  const variantMap: Record<string, GROQButton['type']> = {
    dark: 'default',
    green: 'green',
    outline: 'outline-black',
  };
  const type = variantMap[cta?.variant ?? 'dark'] ?? 'default';

  return {
    type,
    size: 'default',
    shape: 'square',
    cta: groqCta,
  };
}
